import React from 'react';
import { Box, Snackbar, Alert, ToggleButtonGroup, ToggleButton, TextField, Typography } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PeopleIcon from '@mui/icons-material/People';

import { PageHeader, DataTableCard, EntityDrawer, ConfirmDialog, FieldRow, FeatureUsageMeter } from '../ui';
import HelpMark from '../help/HelpMark';
import buildUserColumns from './buildUserColumns';
import useUsersPage from './useUsersPage';

const AllUsers = () => {
  const p = useUsersPage();

  const columns = buildUserColumns({ t: p.t, openEditDrawer: p.openEditDrawer, openDeleteConfirm: p.openDeleteConfirm });

  const isCreate = p.drawerMode === 'create';
  const isExistingInvite = isCreate && p.inviteMode === 'existing';
  // Invite drawer only: on a plan without the User role every seat is an
  // admin seat, so the User toggle is not offered. Editing keeps both.
  const adminOnlySeats = isCreate && !p.userRoleAllowed;
  const adminOnlyHelper = p.t('Every seat on this plan is an admin seat.', { defaultValue: 'Every seat on this plan is an admin seat.' });

  return (
    <Box>
      <PageHeader
        title={p.t('Users')}
        subtitle={p.t('People with access to your workspace and shop', { defaultValue: 'People with access to your workspace and shop' })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search Users', { defaultValue: 'Search users' }) }}
        actions={[
          {
            label: p.t('Invite user', { defaultValue: 'Invite user' }), icon: PersonAddIcon, variant: 'contained', onClick: p.openInviteDrawer,
            disabled: p.planLimit?.atLimit, disabledReason: p.planLimit?.tooltip,
          },
        ]}
        helpKey="users"
        meter={(
          <>
            <FeatureUsageMeter feature="users.seats" label={p.t('Team seats', { defaultValue: 'Team seats' })} />
            {!p.userRoleAllowed && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{adminOnlyHelper}</Typography>
                <HelpMark id="users.admin-only-seats" variant="outlined" />
              </Box>
            )}
          </>
        )}
      />

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        bulkBar={{
          count: p.selection.length,
          countLabel: p.t('{{count}} selected', { count: p.selection.length, defaultValue: `${p.selection.length} selected` }),
          actions: [
            { label: p.t('Remove Selected'), onClick: () => p.openDeleteConfirm(p.selectedUsers) },
          ],
          onClear: () => p.setSelection([]),
        }}
        empty={p.isNoMatch ? {
          kind: 'no-match',
          title: p.t('No users match "{{query}}"', { query: p.search, defaultValue: `No users match "${p.search}"` }),
          description: p.t('Try a different search.', { defaultValue: 'Try a different search.' }),
          actions: [{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); } }],
        } : {
          kind: 'none',
          icon: PeopleIcon,
          title: p.t('No users yet', { defaultValue: 'No users yet' }),
          description: p.t('Invite a teammate to give them access to this workspace.', { defaultValue: 'Invite a teammate to give them access to this workspace.' }),
          actions: [{ label: p.t('Invite user', { defaultValue: 'Invite user' }), variant: 'contained', onClick: p.openInviteDrawer }],
        }}
        error={{
          title: p.t("Couldn't load users", { defaultValue: "Couldn't load users" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: () => p.fetchUsers(true),
        }}
        permission={{
          title: p.t("You don't have access to Users", { defaultValue: "You don't have access to Users" }),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        checkboxSelection
        rowSelectionModel={p.selection}
        onRowSelectionModelChange={p.setSelection}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <EntityDrawer
        open={p.drawerOpen}
        onClose={p.closeDrawer}
        title={isCreate ? p.t('Invite user', { defaultValue: 'Invite user' }) : p.t('Edit user', { defaultValue: 'Edit user' })}
        subtitle={!isCreate
          ? undefined
          : (isExistingInvite
            ? p.t('Add someone who already has a SmartInventory account.', { defaultValue: 'Add someone who already has a SmartInventory account.' })
            : p.t('They sign in with SSO — no password to set.', { defaultValue: 'They sign in with SSO — no password to set.' }))}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        cancelLabel={p.t('Cancel')}
        primaryLabel={p.saving
          ? (isCreate ? p.t('Creating...') : p.t('Saving...', { defaultValue: 'Saving...' }))
          : (isCreate
            ? (isExistingInvite ? p.t('Add', { defaultValue: 'Add' }) : p.t('Create'))
            : p.t('Save', { defaultValue: 'Save' }))}
        onPrimary={p.handleSave}
        primaryDisabled={p.saving}
        loading={p.saving}
      >
        {isCreate && (
          <FieldRow label={p.t('Type', { defaultValue: 'Type' })}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={p.inviteMode}
              onChange={(e, value) => value && p.setInviteMode(value)}
              disabled={p.saving}
            >
              <ToggleButton value="create">{p.t('New account', { defaultValue: 'New account' })}</ToggleButton>
              <ToggleButton value="existing">{p.t('Existing account', { defaultValue: 'Existing account' })}</ToggleButton>
            </ToggleButtonGroup>
          </FieldRow>
        )}

        {isExistingInvite ? (
          <>
            <FieldRow
              label={p.t('Email')}
              error={p.formErrors.email}
              helper={p.formErrors.email ? undefined : p.t('The email of an existing SmartInventory account — from any company.', { defaultValue: 'The email of an existing SmartInventory account — from any company.' })}
              required
            >
              <TextField
                fullWidth
                type="email"
                value={p.existingForm.email}
                onChange={(e) => p.setExistingForm({ ...p.existingForm, email: e.target.value })}
                disabled={p.saving}
                error={Boolean(p.formErrors.email)}
              />
            </FieldRow>

            <FieldRow label={p.t('Role')} helper={adminOnlySeats ? adminOnlyHelper : undefined}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={p.existingForm.role}
                onChange={(e, value) => value && p.setExistingForm({ ...p.existingForm, role: value })}
                disabled={p.saving}
              >
                {!adminOnlySeats && <ToggleButton value="user">{p.t('User', { defaultValue: 'User' })}</ToggleButton>}
                <ToggleButton value="admin">{p.t('Admin')}</ToggleButton>
              </ToggleButtonGroup>
            </FieldRow>
          </>
        ) : (
          <>
            <FieldRow label={p.t('Username')} error={p.formErrors.username} required>
              <TextField
                fullWidth
                value={p.form.username}
                onChange={(e) => p.setForm({ ...p.form, username: e.target.value })}
                disabled={p.saving}
                error={Boolean(p.formErrors.username)}
              />
            </FieldRow>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <FieldRow label={p.t('First Name', { defaultValue: 'First Name' })} error={p.formErrors.firstName} required>
                <TextField
                  fullWidth
                  value={p.form.firstName}
                  onChange={(e) => p.setForm({ ...p.form, firstName: e.target.value })}
                  disabled={p.saving}
                  error={Boolean(p.formErrors.firstName)}
                />
              </FieldRow>

              <FieldRow label={p.t('Last Name', { defaultValue: 'Last Name' })} error={p.formErrors.lastName} required>
                <TextField
                  fullWidth
                  value={p.form.lastName}
                  onChange={(e) => p.setForm({ ...p.form, lastName: e.target.value })}
                  disabled={p.saving}
                  error={Boolean(p.formErrors.lastName)}
                />
              </FieldRow>
            </Box>

            {p.drawerMode === 'edit' && (
              <>
                <FieldRow label={p.t('Company Name')}>
                  <TextField
                    fullWidth
                    value={p.form.billing.companyName}
                    onChange={(e) => p.setForm({ ...p.form, billing: { ...p.form.billing, companyName: e.target.value } })}
                    disabled={p.saving}
                    inputProps={{ maxLength: 150 }}
                  />
                </FieldRow>

                <FieldRow label={p.t('Address')}>
                  <TextField
                    fullWidth
                    value={p.form.billing.addressLine1}
                    onChange={(e) => p.setForm({ ...p.form, billing: { ...p.form.billing, addressLine1: e.target.value } })}
                    disabled={p.saving}
                    inputProps={{ maxLength: 200 }}
                  />
                </FieldRow>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <FieldRow label={p.t('City')}>
                    <TextField
                      fullWidth
                      value={p.form.billing.city}
                      onChange={(e) => p.setForm({ ...p.form, billing: { ...p.form.billing, city: e.target.value } })}
                      disabled={p.saving}
                      inputProps={{ maxLength: 100 }}
                    />
                  </FieldRow>

                  <FieldRow label={p.t('State / Province')}>
                    <TextField
                      fullWidth
                      value={p.form.billing.state}
                      onChange={(e) => p.setForm({ ...p.form, billing: { ...p.form.billing, state: e.target.value } })}
                      disabled={p.saving}
                      inputProps={{ maxLength: 100 }}
                    />
                  </FieldRow>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <FieldRow label={p.t('Postal Code')}>
                    <TextField
                      fullWidth
                      value={p.form.billing.postalCode}
                      onChange={(e) => p.setForm({ ...p.form, billing: { ...p.form.billing, postalCode: e.target.value } })}
                      disabled={p.saving}
                      inputProps={{ maxLength: 20 }}
                    />
                  </FieldRow>

                  <FieldRow label={p.t('Country')}>
                    <TextField
                      fullWidth
                      value={p.form.billing.country}
                      onChange={(e) => p.setForm({ ...p.form, billing: { ...p.form.billing, country: e.target.value } })}
                      disabled={p.saving}
                      inputProps={{ maxLength: 100 }}
                    />
                  </FieldRow>
                </Box>
              </>
            )}

            <FieldRow label={p.t('Email')} error={p.formErrors.email} required>
              <TextField
                fullWidth
                type="email"
                value={p.form.email}
                onChange={(e) => p.setForm({ ...p.form, email: e.target.value })}
                disabled={p.saving}
                error={Boolean(p.formErrors.email)}
              />
            </FieldRow>

            <FieldRow label={p.t('Role')} helper={adminOnlySeats ? adminOnlyHelper : undefined}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={p.form.role}
                onChange={(e, value) => value && p.setForm({ ...p.form, role: value })}
                disabled={p.saving}
              >
                {!adminOnlySeats && <ToggleButton value="user">{p.t('User', { defaultValue: 'User' })}</ToggleButton>}
                <ToggleButton value="admin">{p.t('Admin')}</ToggleButton>
              </ToggleButtonGroup>
            </FieldRow>
          </>
        )}
      </EntityDrawer>

      <ConfirmDialog
        open={p.confirmOpen}
        onClose={() => p.setConfirmOpen(false)}
        tone="danger"
        icon={PersonRemoveIcon}
        title={p.confirmTargets.length === 1
          ? p.t('Remove "{{name}}"?', { name: p.confirmTargets[0]?.username, defaultValue: `Remove "${p.confirmTargets[0]?.username}"?` })
          : p.t('Remove {{count}} users?', { count: p.confirmTargets.length, defaultValue: `Remove ${p.confirmTargets.length} users?` })}
        description={p.t('They will lose access to this workspace immediately.', { defaultValue: 'They will lose access to this workspace immediately.' })}
        records={p.confirmTargets.map((u) => u.username)}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Confirm')}
        onConfirm={p.handleConfirmDelete}
        loading={p.deleting}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllUsers;
